// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useCreateForumMutation, useCreateMessageMutation } from "messaging/_api/messaging-api";
import { validateAttachment } from "messaging/_api/messaging-validation";
import Message from "messaging/message";
import { getMessagingErrorMessage, getMessagingFieldErrors } from "messaging/_api/messaging-error-message";
import Forum from "messaging/forums/forum";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import useInfiniteNetworkConnections from "users/networking/connections/_hooks/use-infinite-network-connections";
import useSelectedNetworkConnections from "users/networking/connections/_hooks/use-selected-network-connections";
// #endregion

async function sendConversationMessage(
  send: () => Promise<void>,
  reportError: (error: unknown) => void,
  finish: () => void,
): Promise<void> {
  try {
    await send();
  } catch (error) {
    reportError(error);
  } finally {
    finish();
  }
}

// #region Styled Components
const ForumContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),
  width: "100%",
}));
const AddSubscriberContainer = styled("div")(() => ({
  minWidth: 0,
  textAlign: "left",
}));
const ForumNameCreate = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginBottom: theme.spacing(1.5),
      marginRight: 0,
    },
  },
  width: "100%",
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const ConnectionListContainer = styled(List)(({ theme }) => ({
  maxHeight: "20rem",
  overflowY: "auto",
  padding: theme.spacing(1, 0),
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
  width: "100%",
}));
// #endregion

// #region Types & Props
type CreateForumDialogProps = {
  onForumCreated: (createdForum: Forum) => void;
  onCancel: () => void;
  composeMessage?: boolean;
  onBusyChange?: (busy: boolean) => void;
};

export default function CreateForumDialog(props: Readonly<CreateForumDialogProps>) {
  const { onForumCreated, onCancel } = props;
  const composeMessage = props.composeMessage ?? false;
  const onBusyChange = props.onBusyChange;

  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [forumNameValue, setForumNameValue] = useState<string>("");
  const [subscriberUserIds, setSubscriberUserIds] = useState<string[]>([]);
  const [erroredUserIds, setErroredUserIds] = useState<string[]>([]);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [messageText, setMessageText] = useState("");
  const [attachment, setAttachment] = useState<File | undefined>();
  const [isSending, setIsSending] = useState(false);
  const [createdConversation, setCreatedConversation] = useState<Forum | undefined>();
  const messageId = useRef(Guid.generate());
  const createOperationId = useRef(Guid.generate());
  const connectionListRef = useRef<HTMLUListElement>(null);

  // #region Hooks
  const session = useSession();
  const [createForum] = useCreateForumMutation();
  const [createMessage] = useCreateMessageMutation();
  // A stable query: the hook starts again from the first page whenever this identity changes.
  const connectionQuery = useMemo(
    () => new NetworkConnectionQuery({ query: debouncedSearchTerm }),
    [debouncedSearchTerm]
  );
  const {
    connections,
    isLoading: isLoadingConnections,
    isInitialLoading: isInitialLoadingConnections,
    loadMore,
  } = useInfiniteNetworkConnections(connectionQuery);
  const networkConnections = connections.data;
  useInfiniteScroll(loadMore, connections.hasNextPage, isLoadingConnections, connectionListRef);

  // The selected people that a new search or an unloaded page has taken out of the list. The
  // array is memoized because the hook keys its effect on it.
  const unlistedSubscriberIds = useMemo(
    () =>
      subscriberUserIds
        .filter((userId) => !networkConnections.some((connection) => connection.id?.value === userId))
        .map((userId) => new Guid(userId)),
    [networkConnections, subscriberUserIds]
  );
  const unlistedSubscribers = useSelectedNetworkConnections(unlistedSubscriberIds);

  // #region Functions
  useEffect(() => {
    onBusyChange?.(isSaving || isSending);
  }, [isSaving, isSending, onBusyChange]);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    const debounceHandle = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, trimmedSearchTerm ? 300 : 0);

    return () => globalThis.clearTimeout(debounceHandle);
  }, [searchTerm]);

  async function saveChanges(): Promise<Forum | undefined> {
    if (!session.user?.id || !session.user?.legalEntityId) return;
    if (!composeMessage && !forumNameValue.trim()) {
      setFieldError("Conversation name is required.");
      return;
    }
    if (subscriberUserIds.length < 1) {
      setFieldError("Select at least one subscriber.");
      return;
    }
    setFieldError(undefined);

    setIsSaving(true);
    try {
      const subscribers: EntityMember[] = [];
      const missingUserIds: string[] = [];

      for (const userId of subscriberUserIds) {
        const connection = findSubscriberConnection(userId);
        if (!connection?.id || !connection.legalEntityId) {
          missingUserIds.push(userId);
          continue;
        }
        subscribers.push(
          EntityMember.create({
            userId: connection.id,
            legalEntityId: connection.legalEntityId,
          })
        );
      }

      if (missingUserIds.length > 0) {
        setErroredUserIds(missingUserIds);
        setFieldError("Unable to load some selected subscribers. Remove them and try again.");
        enqueueSnackbar(
          "Unable to find information for some selected subscribers. Please remove them and try again.",
          { variant: "warning" }
        );
        return;
      }

      const newForum = Forum.create({
        name: new ForumName(forumNameValue.trim() || subscriberUserIds
          .map(userId => findSubscriberConnection(userId)?.name.fullName)
          .filter(Boolean).join(", ")),
        subscribers: subscribers,
        createdDate: new AHDate(),
        creator: EntityMember.create({
          userId: session.user.id,
          legalEntityId: session.user.legalEntityId,
        }),
        numUnreadMessages: 0,
        topic: Topic.create({
          entityClass: new ForumEntityClass("userCreatedChat"),
          context: "UserCreatedChat",
        }),
      });
      const createdForum = Forum.fromJSON(
        await createForum({ forum: newForum, operationId: createOperationId.current }).unwrap()
      );
      if (!createdForum) throw new Error("Created forum response was invalid");
      if (!composeMessage) onForumCreated(createdForum);
      return createdForum;
    } catch (error) {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.name ?? fieldErrors.topic ?? fieldErrors.subscribers);
      enqueueSnackbar(getMessagingErrorMessage(error, "We could not create the conversation. Please try again."), { variant: "error" });
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }

  async function sendFirstMessage(): Promise<void> {
    const sender = session.user;
    if (isSending || !sender || (!messageText.trim() && !attachment)) return;
    setIsSending(true);
    setFieldError(undefined);
    await sendConversationMessage(async () => {
      // Retain the created conversation when the message fails so retry cannot create another one.
      const forum = createdConversation ?? await saveChanges();
      if (!forum) return;
      setCreatedConversation(forum);
      const message = Message.draft({
        id: messageId.current,
        created: new AHDate(),
        forum,
        sender,
        content: messageText,
        attachments: attachment ? [attachment] : [],
      });
      message.publish();
      await createMessage({ message }).unwrap();
      onForumCreated(forum);
    }, (error) => {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.contentPlainText ?? fieldErrors.attachments);
      enqueueSnackbar(getMessagingErrorMessage(error, "We could not send your message. Please try again."), { variant: "error" });
    }, () => {
      setIsSending(false);
    });
  }

  function handleSubscriberSelected(
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    subscriberUserId: string
  ): void {
    if (isSaving || isSending || createdConversation) return;
    const selectedSubscriberIds = [...subscriberUserIds];
    const index = selectedSubscriberIds.indexOf(subscriberUserId);
    if (index === -1) {
      selectedSubscriberIds.push(subscriberUserId);
    } else {
      selectedSubscriberIds.splice(index, 1);
    }
    setSubscriberUserIds(selectedSubscriberIds);
  }

  function findSubscriberConnection(userId: string): UserInformation | undefined {
    const listedConnection = networkConnections.find((networkConnection) => networkConnection.id?.value === userId);
    if (listedConnection) return listedConnection;
    const unlistedSubscriber = unlistedSubscribers.find((subscriber) => subscriber.userId.value === userId);
    return unlistedSubscriber?.state === "found" ? unlistedSubscriber.connection : undefined;
  }

  function isSelected(subscriberUserId: string): boolean {
    return subscriberUserIds.includes(subscriberUserId);
  }

  // #region Variables
  const saveDisabled: boolean =
    // Selected people stay selected when a search hides them, so an empty search result does not
    // stop a save.
    subscriberUserIds.length < 1 ||
    isSaving ||
    (!composeMessage && !forumNameValue.trim()) ||
    isSending ||
    (composeMessage && !messageText.trim() && !attachment) ||
    !session.user?.id ||
    !session.user?.legalEntityId;
  const isSearching = searchTerm.trim().length > 0 || debouncedSearchTerm.trim().length > 0;
  const isInitialLoading = isInitialLoadingConnections && !isSearching;
  const canCreateForum: boolean = networkConnections.length > 0 || isSearching;

  // #region Render
  return (
    <React.Fragment>
      {isInitialLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isInitialLoading && !canCreateForum && (
        <LoaderContainer>
          <Typography>You are not networked with anyone</Typography>
        </LoaderContainer>
      )}
      {!isInitialLoading && canCreateForum && (
        <>
          <ForumContainer>
            {!composeMessage && <ForumNameCreate
              label="Conversation name"
              required={true}
              onChange={(e) => {
                setForumNameValue(e.target.value);
                if (e.target.value.trim()) setFieldError(undefined);
              }}
              value={forumNameValue}
              error={Boolean(fieldError && !forumNameValue.trim())}
              helperText={fieldError && !forumNameValue.trim() ? fieldError : "required"}
            />}
            <AddSubscriberContainer>
              <SearchField
                size="small"
                variant={composeMessage ? "outlined" : "standard"}
                placeholder="Search Connections"
                label={composeMessage ? "To" : undefined}
                disabled={isSaving || isSending || Boolean(createdConversation)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="disabled" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {searchTerm.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm("");
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </>
                    ),
                  },
                }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <ConnectionListContainer dense ref={connectionListRef} sx={composeMessage ? { maxHeight: 180 } : undefined}>
                {unlistedSubscribers.map((subscriber) => {
                  const hasError = erroredUserIds.includes(subscriber.userId.value);
                  return (
                    <ListItemButton
                      disabled={isSaving || isSending || Boolean(createdConversation)}
                      disableGutters
                      key={subscriber.userId.value}
                      selected={true}
                      onClick={(event) => handleSubscriberSelected(event, subscriber.userId.value)}
                    >
                      <ListItemAvatar>
                        <SelectedIcon>
                          <DoneIcon />
                        </SelectedIcon>
                      </ListItemAvatar>
                      <ListItemText
                        primary={hasError ? `${subscriber.name} (Error Loading)` : subscriber.name}
                        secondary={subscriber.state === "found" ? (subscriber.connection.email?.value ?? "") : ""}
                        slotProps={{
                          primary: {
                            color: hasError ? "error" : "textPrimary",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
                {networkConnections.length < 1 && isSearching && !isLoadingConnections && (
                  <ListItem>
                    <ListItemText
                      primary="No matching connections"
                      slotProps={{ primary: { sx: { textAlign: "center", padding: 1, typography: "body1" } } }}
                    />
                  </ListItem>
                )}
                {networkConnections.map((connection: UserInformation) => {
                  const hasError = erroredUserIds.includes(connection.id?.value ?? "");
                  return (
                    <ListItemButton
                      disabled={isSaving || isSending || Boolean(createdConversation)}
                      disableGutters
                      key={connection.id?.value}
                      selected={isSelected(connection.id.value)}
                      onClick={(event) => handleSubscriberSelected(event, connection.id.value)}
                    >
                      <ListItemAvatar>
                        {isSelected(connection.id.value) ? (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        ) : (
                          <IndividualAvatar individual={connection.toIndividual()} />
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          hasError ? `${connection.name.fullName} (Error Loading)` : connection.name.fullName
                        }
                        secondary={connection.email?.value ?? ""}
                        slotProps={{
                          primary: {
                            color: hasError ? "error" : "textPrimary",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </ConnectionListContainer>
            </AddSubscriberContainer>
          </ForumContainer>
          {composeMessage && (
            <>
              <TextField
                label="Message"
                placeholder="Write your message…"
                fullWidth
                multiline
                minRows={5}
                disabled={isSaving || isSending}
                value={messageText}
                onChange={event => { setMessageText(event.target.value); setFieldError(undefined); }}
              />
              <details style={{ margin: "16px 0" }}>
                <summary>Add a conversation name (optional)</summary>
                <ForumNameCreate
                  label="Conversation name"
                  value={forumNameValue}
                  disabled={isSaving || isSending || Boolean(createdConversation)}
                  onChange={event => setForumNameValue(event.target.value)}
                  helperText="Leave blank to use the participants’ names."
                  sx={{ mt: 1.5 }}
                />
              </details>
              {attachment && <Button className={SESSION_RECORDING_MASK_CLASS} aria-label={`Remove attachment ${attachment.name}`} disabled={isSending || isSaving} onClick={() => setAttachment(undefined)} endIcon={<CloseIcon />}>{attachment.name}</Button>}
            </>
          )}
          {fieldError && (composeMessage || forumNameValue.trim()) && <Typography role="alert" color="error">{fieldError}</Typography>}
          <ButtonContainer>
            {composeMessage && <Button component="label" startIcon={<AttachFileIcon />} sx={{ mr: "auto" }} disabled={isSending || isSaving}>
                Attach
                <input type="file" hidden aria-label="Attach a file" onChange={event => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file) return;
                  const error = validateAttachment(file);
                  if (error) { enqueueSnackbar(error, { variant: "error" }); return; }
                  setAttachment(file);
                  setFieldError(undefined);
                }} />
              </Button>}
            <Button disabled={isSending || isSaving} onClick={onCancel}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={composeMessage ? <SendIcon /> : <SaveIcon />}
              loading={isSaving || isSending}
              disabled={saveDisabled}
              onClick={() => { void (composeMessage ? sendFirstMessage() : saveChanges()); }}
            >
              {composeMessage ? "Send" : "Save"}
            </LoadingButton>
          </ButtonContainer>
        </>
      )}
    </React.Fragment>
  );
}
